import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api';

// Google Identity Services 型別宣告
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: object) => void;
        };
      };
    };
  }
}

interface LoginProps {
  onLogin: () => void;
}

// Google OAuth Client ID
const GOOGLE_CLIENT_ID = (import.meta as unknown as { env: { VITE_GOOGLE_CLIENT_ID?: string } }).env.VITE_GOOGLE_CLIENT_ID || '';

export default function Login({ onLogin }: LoginProps) {
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  // 初始化 Google Sign-In
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      if (window.google && GOOGLE_CLIENT_ID) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });

        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          window.google.accounts.id.renderButton(buttonDiv, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'signin_with',
            shape: 'pill',
            width: 320,
          });
        }
      }
    };

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google OAuth 回調
  const handleGoogleResponse = async (response: { credential: string }) => {
    setGoogleLoading(true);
    setError('');

    try {
      const res = await apiFetch('/api/auth/google', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });

      if (res.ok) {
        onLogin();
      } else {
        const data = await res.json();
        setError(data.error || 'Google 登入失敗');
      }
    } catch {
      setError('網路錯誤，請稍後再試');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4 relative overflow-hidden">
      {/* 環境光球背景 */}
      <div
        className="absolute w-96 h-96 rounded-full blur-3xl animate-ambient"
        style={{
          background: 'radial-gradient(circle, rgba(233, 30, 140, 0.15) 0%, transparent 70%)',
          top: '10%',
          left: '20%',
        }}
      />
      <div
        className="absolute w-80 h-80 rounded-full blur-3xl animate-ambient"
        style={{
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%)',
          bottom: '20%',
          right: '15%',
          animationDelay: '5s',
        }}
      />
      <div
        className="absolute w-64 h-64 rounded-full blur-3xl animate-ambient"
        style={{
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)',
          top: '50%',
          right: '30%',
          animationDelay: '10s',
        }}
      />

      {/* 登入卡片 */}
      <div className="w-full max-w-sm relative z-10 animate-fadeUp">
        {/* Logo 區域 */}
        <div className="text-center mb-10">
          {/* 呼吸光球 */}
          <div className="relative w-28 h-28 mx-auto mb-6">
            <div
              className="absolute inset-0 rounded-full animate-breathe"
              style={{
                background: 'radial-gradient(circle, rgba(233, 30, 140, 0.4) 0%, rgba(233, 30, 140, 0.1) 50%, transparent 70%)',
              }}
            />
            <div
              className="absolute inset-4 rounded-full animate-breathe"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c 50%, #c41874)',
                boxShadow: '0 0 60px rgba(233, 30, 140, 0.5)',
                animationDelay: '0.2s',
              }}
            />
            <div
              className="absolute w-3 h-3 rounded-full bg-white/80"
              style={{
                top: '28%',
                left: '32%',
                filter: 'blur(1px)',
              }}
            />
          </div>

          <h1 className="text-3xl font-semibold text-text-primary mb-2 tracking-wide">
            Felicity
          </h1>
          <p className="text-text-secondary text-sm">
            包小容 | 智能管理中台
          </p>
        </div>

        {/* 登入卡片 */}
        <div
          className="rounded-2xl p-8"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 0 80px rgba(233, 30, 140, 0.08)',
          }}
        >
          {error && (
            <div
              className="p-4 rounded-xl text-sm mb-6"
              style={{
                background: 'rgba(255, 82, 82, 0.1)',
                border: '1px solid rgba(255, 82, 82, 0.2)',
                color: '#ff5252',
              }}
            >
              {error}
            </div>
          )}

          {/* Google 登入按鈕 */}
          <div
            id="google-signin-button"
            className="w-full flex justify-center"
            style={{ minHeight: '44px' }}
          />

          {/* 載入中 */}
          {googleLoading && (
            <div className="w-full h-12 rounded-xl flex items-center justify-center gap-2 mt-4"
              style={{
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            >
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              登入中...
            </div>
          )}

          {/* Google Client ID 未設定時的提示 */}
          {!GOOGLE_CLIENT_ID && (
            <div
              className="text-center text-sm p-4 rounded-xl"
              style={{
                background: 'rgba(255, 170, 0, 0.1)',
                border: '1px solid rgba(255, 170, 0, 0.2)',
                color: '#ffaa00',
              }}
            >
              Google 登入尚未設定
            </div>
          )}
        </div>

        {/* 底部文字 */}
        <p className="text-center text-text-muted text-xs mt-8 tracking-wide">
          打開這個頁面，就像打開女兒的房間門
        </p>
      </div>
    </div>
  );
}
